import React, { useEffect, useState } from "react";
import "../../assets/styles/card.css";
import "../../assets/styles/toggle.css";
import BASE_URL from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const SLOT_DEFAULT = 0;
const SLOT_INDOOR = 1;
const SLOT_OUTDOOR = 2;
const SLOT_CUSTOM = 3;

const FALLBACK_LABELS = {
  [SLOT_INDOOR]: "Indoor",
  [SLOT_OUTDOOR]: "Outdoor",
  [SLOT_CUSTOM]: "Custom",
};

function AccessibilitySettings() {
  const [loading, setLoading] = useState(true);
  const [ageGroup, setAgeGroup] = useState("");
  const [levelOfBlindness, setLevelOfBlindness] = useState("");
  const [blindFromBirth, setBlindFromBirth] = useState(null);
  const [colorBlind, setColorBlind] = useState(null);

  // Custom scene-description prompt: exactly one of Default / Indoor /
  // Outdoor / Custom is active at a time. Default means "use the app's
  // normal preconfigured prompt" (current/unchanged behavior) -- it has no
  // text of its own. Indoor/Outdoor/Custom each have their own saved text
  // in custom_prompt_1/2/3, and picking one replaces the preconfigured
  // prompt with that text for scene description.
  const [selectedSlot, setSelectedSlot] = useState(SLOT_DEFAULT);
  const [customPromptText, setCustomPromptText] = useState({
    [SLOT_INDOOR]: "",
    [SLOT_OUTDOOR]: "",
    [SLOT_CUSTOM]: "",
  });
  const [customPromptOptions, setCustomPromptOptions] = useState([]);
  const [promptError, setPromptError] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);

  useEffect(() => {
    const fetchPreference = async () => {
      try {
        const token = localStorage.getItem("jwt_token");
        const response = await fetch(
          `${BASE_URL}/api/preferences/get-my-preferred-mode/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setAgeGroup(data.age_group || "");
          setLevelOfBlindness(data.level_of_blindness || "");
          setBlindFromBirth(data.blind_from_birth);
          setColorBlind(data.color_blind);
          const slot = data.selected_custom_prompt_slot ?? SLOT_DEFAULT;
          setSelectedSlot(slot);
          setCustomPromptText({
            [SLOT_INDOOR]: data.custom_prompt_1 ?? "",
            [SLOT_OUTDOOR]: data.custom_prompt_2 ?? "",
            [SLOT_CUSTOM]: data.custom_prompt_3 ?? "",
          });
        } else {
          console.error("Failed to fetch preference");
        }
      } catch (error) {
        console.error("Error fetching preference:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCustomPromptOptions = async () => {
      try {
        const token = localStorage.getItem("jwt_token");
        const response = await fetch(
          `${BASE_URL}/api/preferences/custom-prompt-options/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setCustomPromptOptions(Array.isArray(data.data) ? data.data : []);
        }
      } catch (error) {
        console.error("Error fetching custom prompt options:", error);
      }
    };

    fetchPreference();
    fetchCustomPromptOptions();
  }, []);

  const patchPreference = async (key, value, setter) => {
    try {
      const token = localStorage.getItem("jwt_token");
      const response = await fetch(
        `${BASE_URL}/api/preferences/update-preference-mode/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ [key]: value }),
        }
      );

      if (response.ok) {
        setter(value);
        toast.success(`${key.replace(/_/g, " ")} updated!`);
      } else {
        toast.error(`Failed to update ${key}`);
      }
    } catch (error) {
      console.error(`Error updating ${key}:`, error);
      toast.error(`Error updating ${key}`);
    }
  };

  const optionMeta = (slot) => {
    const found = customPromptOptions.find((opt) => opt.slot === slot);
    return {
      label: found?.label ?? FALLBACK_LABELS[slot],
      placeholder: found?.value ?? `Enter your ${FALLBACK_LABELS[slot].toLowerCase()} prompt...`,
    };
  };

  const handleSelectSlot = (slot) => {
    setPromptError("");
    setSelectedSlot(slot);
  };

  const handlePromptTextChange = (slot, value) => {
    setPromptError("");
    setCustomPromptText((prev) => ({ ...prev, [slot]: value }));
  };

  const handleSavePrompt = async () => {
    if (selectedSlot !== SLOT_DEFAULT) {
      const text = (customPromptText[selectedSlot] || "").trim();
      if (!text) {
        setPromptError(
          `Please enter a ${optionMeta(selectedSlot).label.toLowerCase()} prompt before saving -- it can't be left empty while selected.`
        );
        return;
      }
    }

    setSavingPrompt(true);
    setPromptError("");
    try {
      const token = localStorage.getItem("jwt_token");
      const response = await fetch(
        `${BASE_URL}/api/preferences/update-preference-mode/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            selected_custom_prompt_slot: selectedSlot,
            custom_prompt_1: customPromptText[SLOT_INDOOR],
            custom_prompt_2: customPromptText[SLOT_OUTDOOR],
            custom_prompt_3: customPromptText[SLOT_CUSTOM],
          }),
        }
      );

      if (response.ok) {
        toast.success("Prompt settings updated!");
      } else {
        const errData = await response.json().catch(() => null);
        const message =
          errData?.data?.non_field_errors?.[0] ||
          errData?.non_field_errors?.[0] ||
          errData?.custom_prompt_1?.[0] ||
          errData?.custom_prompt_2?.[0] ||
          errData?.custom_prompt_3?.[0] ||
          errData?.selected_custom_prompt_slot?.[0] ||
          "Failed to update prompt settings";
        setPromptError(message);
        toast.error(message);
      }
    } catch (error) {
      console.error("Error updating prompt settings:", error);
      toast.error("Error updating prompt settings");
    } finally {
      setSavingPrompt(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      <div className="pref-card-container">
        <h2 className="pref-card-title">Personal Settings</h2>
        <p className="pref-card-description">
          Configure your personal preferences to personalise your experience.
        </p>
        {loading ? (
          <p className="pref-card-loading">Loading...</p>
        ) : (
          <>
            <div className="pref-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
              <span className="pref-card-mode">Age Group</span>
              <select
                value={ageGroup}
                onChange={(e) =>
                  patchPreference("age_group", e.target.value || null, setAgeGroup)
                }
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '2px solid #667eea',
                  fontSize: '14px',
                  background: 'linear-gradient(135deg, #f8f9ff 0%, #ede9fe 100%)',
                  color: '#2d3748',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  outline: 'none',
                  fontWeight: '500',
                  appearance: 'auto',
                  WebkitAppearance: 'auto',
                  MozAppearance: 'auto',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#764ba2';
                  e.target.style.boxShadow = '0 4px 12px rgba(118, 75, 162, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.15)';
                }}
              >
                <option value="" style={{ backgroundColor: '#fff', color: '#666' }}>Select Age Group</option>
                <option value="0-5" style={{ backgroundColor: '#fff', color: '#2d3748' }}>0-5 years</option>
                <option value="5-10" style={{ backgroundColor: '#fff', color: '#2d3748' }}>5-10 years</option>
                <option value="10-15" style={{ backgroundColor: '#fff', color: '#2d3748' }}>10-15 years</option>
                <option value="15-20" style={{ backgroundColor: '#fff', color: '#2d3748' }}>15-20 years</option>
                <option value="20-30" style={{ backgroundColor: '#fff', color: '#2d3748' }}>20-30 years</option>
                <option value="30-40" style={{ backgroundColor: '#fff', color: '#2d3748' }}>30-40 years</option>
                <option value="40-50" style={{ backgroundColor: '#fff', color: '#2d3748' }}>40-50 years</option>
                <option value="50-60" style={{ backgroundColor: '#fff', color: '#2d3748' }}>50-60 years</option>
                <option value="60-70" style={{ backgroundColor: '#fff', color: '#2d3748' }}>60-70 years</option>
                <option value="70+" style={{ backgroundColor: '#fff', color: '#2d3748' }}>70+ years</option>
              </select>
            </div>

            <div className="pref-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
              <span className="pref-card-mode">Level of Blindness</span>
              <select
                value={levelOfBlindness}
                onChange={(e) =>
                  patchPreference("level_of_blindness", e.target.value || null, setLevelOfBlindness)
                }
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '2px solid #667eea',
                  fontSize: '14px',
                  background: 'linear-gradient(135deg, #f8f9ff 0%, #ede9fe 100%)',
                  color: '#2d3748',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  outline: 'none',
                  fontWeight: '500',
                  appearance: 'auto',
                  WebkitAppearance: 'auto',
                  MozAppearance: 'auto',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.15)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#764ba2';
                  e.target.style.boxShadow = '0 4px 12px rgba(118, 75, 162, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.15)';
                }}
              >
                <option value="" style={{ backgroundColor: '#fff', color: '#666' }}>Select Level</option>
                <option value="functional" style={{ backgroundColor: '#fff', color: '#2d3748' }}>Functional Vision</option>
                <option value="moderate" style={{ backgroundColor: '#fff', color: '#2d3748' }}>Moderate Vision Loss</option>
                <option value="low_vision" style={{ backgroundColor: '#fff', color: '#2d3748' }}>Low Vision</option>
                <option value="light_perception" style={{ backgroundColor: '#fff', color: '#2d3748' }}>Light Perception Only</option>
                <option value="totally_blind" style={{ backgroundColor: '#fff', color: '#2d3748' }}>Totally Blind</option>
              </select>
            </div>

            <div className="pref-row pref-custom-prompt-section">
              <span className="pref-card-mode">Scene Description Prompt</span>
              <p className="pref-custom-prompt-hint">
                Choose which prompt is used when describing your surroundings.
                "Default" uses the app's built-in prompt. Choosing Indoor,
                Outdoor, or Custom uses only that prompt's text instead --
                exactly one option is active at a time, and its text can't be
                left empty.
              </p>

              {/* Default */}
              <div
                className={`pref-custom-prompt-item${selectedSlot === SLOT_DEFAULT ? " is-selected" : ""}`}
              >
                <div className="pref-custom-prompt-row">
                  <input
                    type="radio"
                    className="pref-custom-prompt-checkbox"
                    name="customPromptSlot"
                    checked={selectedSlot === SLOT_DEFAULT}
                    onChange={() => handleSelectSlot(SLOT_DEFAULT)}
                  />
                  <div>
                    <div className="pref-custom-prompt-label">Default</div>
                  </div>
                </div>
              </div>

              {/* Indoor / Outdoor / Custom */}
              {[SLOT_INDOOR, SLOT_OUTDOOR, SLOT_CUSTOM].map((slot) => {
                const meta = optionMeta(slot);
                const isSelected = selectedSlot === slot;
                return (
                  <div
                    key={slot}
                    className={`pref-custom-prompt-item${isSelected ? " is-selected" : ""}`}
                  >
                    <div className="pref-custom-prompt-row">
                      <input
                        type="radio"
                        className="pref-custom-prompt-checkbox"
                        name="customPromptSlot"
                        checked={isSelected}
                        onChange={() => handleSelectSlot(slot)}
                      />
                      <div>
                        <div className="pref-custom-prompt-label">{meta.label}</div>
                        <textarea
                          className="pref-custom-prompt-textarea"
                          value={customPromptText[slot]}
                          placeholder={meta.placeholder}
                          onChange={(e) => handlePromptTextChange(slot, e.target.value)}
                          rows={4}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              {promptError && (
                <p style={{ color: "#e53e3e", fontSize: "13px", margin: 0 }}>
                  {promptError}
                </p>
              )}

              <button
                type="button"
                onClick={handleSavePrompt}
                disabled={savingPrompt}
                style={{
                  alignSelf: "flex-start",
                  padding: "10px 20px",
                  borderRadius: "10px",
                  border: "none",
                  background: savingPrompt
                    ? "#a0aec0"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  fontWeight: 600,
                  cursor: savingPrompt ? "not-allowed" : "pointer",
                }}
              >
                {savingPrompt ? "Saving..." : "Save"}
              </button>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Blind From Birth: &nbsp;{" "}
                <strong>
                  {blindFromBirth === null
                    ? "Not Set"
                    : blindFromBirth
                    ? "Yes"
                    : "No"}
                </strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  checked={blindFromBirth === true}
                  onChange={() =>
                    patchPreference(
                      "blind_from_birth",
                      blindFromBirth === true ? false : true,
                      setBlindFromBirth
                    )
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>

            <div className="pref-row">
              <span className="pref-card-mode">
                Color Blind: &nbsp;{" "}
                <strong>
                  {colorBlind === null ? "Not Set" : colorBlind ? "Yes" : "No"}
                </strong>
              </span>
              <label className="pref-toggle-switch small">
                <input
                  type="checkbox"
                  checked={colorBlind === true}
                  onChange={() =>
                    patchPreference(
                      "color_blind",
                      colorBlind === true ? false : true,
                      setColorBlind
                    )
                  }
                />
                <span className="pref-slider small"></span>
              </label>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default AccessibilitySettings;
